import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText } from "lucide-react";

export default function ProductDetails({ product, onClose }) {
  if (!product) return null;

  return (
    <AnimatePresence>
      <motion.button
        type="button"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 z-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.28 }}
        className="fixed inset-y-0 right-0 z-50 md:w-[40%] w-full bg-surface shadow-2xl border-l overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink text-start">Product details</h2>
            <p className="max-w-[260px] truncate text-start text-xs text-muted">{product.name}</p>
          </div>
          <button onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
            <svg className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Images</h3>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(product.images || []).map((img, i) => (
                <img key={i} src={img || "/placeholder.svg"} alt={`${product.name} ${i + 1}`} className="rounded-lg aspect-square object-cover w-full" />
              ))}
            </div>
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Fiche Technique</h3>
            {product.fiche_technique && (
              <a
                href={product.fiche_technique}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-xs font-medium uppercase text-accent-fg transition hover:bg-accent-strong"
              >
                <FileText size={16} />
                Voir le PDF
              </a>
            )}
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Basic Information</h3>
            <dl className="mt-2 grid grid-cols-1 gap-2">
              <div>
                <dt className="text-xs text-muted text-start">Name</dt>
                <dd className="text-sm text-ink text-start">{product.name}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted text-start">Description</dt>
                <dd className="text-sm text-ink text-start">{product.description}</dd>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <dt className="text-xs text-muted text-start">Reference</dt>
                  <dd className="text-sm text-ink text-start">{product.reference}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted text-start">SKU</dt>
                  <dd className="text-sm text-ink text-start">{product.sku}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted text-start">Type</dt>
                  <dd className="text-sm text-ink text-start">{product.type}</dd>
                </div>
              </div>
            </dl>
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Pricing & Stock</h3>
            <dl className="mt-2 grid grid-cols-3 gap-4">
              <div>
                <dt className="text-xs text-muted text-start">
                  {product.type === "demander_devis" ? "Internal price" : "Price"}
                </dt>
                <dd className="text-sm text-ink text-start">
                  {product.has_variants
                    ? "Per variant"
                    : product.price != null
                    ? `$${Number.parseFloat(product.price).toFixed(2)}`
                    : "-"}
                  {product.type === "demander_devis" && <span className="ml-1 text-xs text-muted">· devis</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted text-start">Compare Price</dt>
                <dd className="text-sm text-ink text-start">
                  {product.compare_price ? `$${Number.parseFloat(product.compare_price).toFixed(2)}` : "-"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted text-start">Stock</dt>
                <dd className="text-sm text-ink text-start">{product.stock}</dd>
              </div>
            </dl>
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Category &amp; Brand</h3>
            <dl className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <dt className="text-xs text-muted text-start">Categories</dt>
                <dd className="text-sm text-ink text-start">
                  {(product.categories?.length
                    ? product.categories.map((c) => c.name)
                    : [product.category?.name].filter(Boolean)
                  ).join(", ") || "-"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted text-start">Brand</dt>
                <dd className="text-sm text-ink text-start">{product.brand?.name || "-"}</dd>
              </div>
            </dl>
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Details / Specifications</h3>
            {(() => {
              const rows = Array.isArray(product?.details)
                ? product.details.filter((d) => d && (d.key || d.value))
                : [];
              if (rows.length === 0) {
                return <p className="mt-2 text-sm text-faint text-start">No specifications.</p>;
              }
              return (
                <dl className="mt-2 divide-y divide-line">
                  {rows.map((d, i) => (
                    <div key={i} className="flex justify-between gap-4 py-2 text-sm">
                      <dt className="text-muted text-start">{d.key}</dt>
                      <dd className="text-ink text-right font-medium">{d.value}</dd>
                    </div>
                  ))}
                </dl>
              );
            })()}
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Variants</h3>
            <div className="mt-2 space-y-3">
              {(product.variants || []).length === 0 && (
                <p className="text-sm text-muted text-start">No variants — this is a simple product.</p>
              )}
              {(product.variants || []).map((v) => {
                const label = (v.optionValues || v.option_values || [])
                  .map((ov) => `${ov.option?.name}: ${ov.value}`)
                  .join("  /  ");
                return (
                  <div key={v.id} className="bg-surface border border-line rounded-lg px-4 py-3">
                    <div className="text-sm font-medium text-ink text-start">{label || "Variant"}</div>
                    <dl className="mt-2 grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <dt className="text-muted text-start">Price</dt>
                        <dd className="text-ink text-start">
                          {v.price != null ? `$${Number.parseFloat(v.price).toFixed(2)}` : "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted text-start">Stock</dt>
                        <dd className="text-ink text-start">{v.stock}</dd>
                      </div>
                      <div>
                        <dt className="text-muted text-start">SKU</dt>
                        <dd className="text-ink text-start">{v.sku || "-"}</dd>
                      </div>
                    </dl>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bg-surface-2 p-4 rounded-xl">
            <h3 className="text-sm font-medium text-ink text-start">Status</h3>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[
                { k: "is_active", label: "Active" },
                { k: "is_top_seller", label: "Top Seller" },
                { k: "is_featured", label: "Featured" },
                { k: "is_special_offer", label: "Special Offer" },
              ].map(({ k, label }) => (
                <div key={k} className="flex items-center">
                  <span className={`h-2.5 w-2.5 rounded-full mr-2 ${product[k] ? "bg-green-500" : "bg-neutral-300"}`} />
                  <span className="text-sm text-ink text-start">{label}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
